import { Box } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Team } from "../interfaces";
import { getTeamCardStyles } from "../theme/siteTheme";

interface TeamCardProps {
  team: Team;
  onShowTeamSchedule?: (teamId: string) => void;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, onShowTeamSchedule }) => {
  const theme = useTheme();
  const styles = getTeamCardStyles(theme);

  return (
    <Card
      sx={{
        ...styles.card,
        cursor: onShowTeamSchedule ? "pointer" : "default",
      }}
      onClick={
        onShowTeamSchedule ? () => onShowTeamSchedule(String(team.id)) : undefined
      }
    >
      <CardContent sx={styles.cardContent}>
        <Typography variant="h5" sx={styles.title}>
          {team.name}
        </Typography>
        {team.spielort && (
          <Typography sx={styles.bodyText}>
            <strong>Spielort:</strong>{" "}
            {team.spielort.link ? (
              <Tooltip title="Spielort ansehen" arrow placement="right">
                <Box
                  component="a"
                  href={team.spielort.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={styles.link}
                >
                  {team.spielort.name}
                </Box>
              </Tooltip>
            ) : (
              team.spielort.name
            )}
          </Typography>
        )}
        {team.spielort && (
          <Typography sx={styles.bodyText}>
            <strong>Adresse:</strong>{" "}
            <Tooltip title="in google maps öffnen" arrow placement="right">
              <Box
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${team.spielort.name}, ${team.spielort.street}, ${team.spielort.plzOrt}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.link}
              >
                {team.spielort.street}, {team.spielort.plzOrt}
              </Box>
            </Tooltip>
          </Typography>
        )}
        {team.spielbeginn && (
          <Typography sx={styles.bodyText}>
            <strong>Spielbeginn:</strong> {team.spielbeginn}
          </Typography>
        )}
        {team.kapitaen && (
          <Typography sx={styles.bodyText}>
            <strong>Kapitän:</strong> {team.kapitaen}
          </Typography>
        )}
      </CardContent>
      {team.links && team.links.length > 0 && (
        <Box sx={styles.socialRow}>
          {team.links.map((link) => {
            let icon = null;
            if (link.link_type === "facebook") {
              icon = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.406.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
                </svg>
              );
            } else if (link.link_type === "instagram") {
              icon = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.808 2.256 6.088 2.243 6.497 2.243 12c0 5.503.013 5.912.072 7.192.059 1.276.353 2.449 1.32 3.416.967.967 2.14 1.261 3.416 1.32 1.28.059 1.689.072 7.192.072s5.912-.013 7.192-.072c1.276-.059 2.449-.353 3.416-1.32.967-.967 1.261-2.14 1.32-3.416.059-1.28.072-1.689.072-7.192s-.013-5.912-.072-7.192c-.059-1.276-.353-2.449-1.32-3.416C21.449.425 20.276.131 19 .072 17.72.013 17.311 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                </svg>
              );
            } else if (link.link_type === "website") {
              icon = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 2c1.657 0 3.156.672 4.242 1.758A5.978 5.978 0 0 1 19.584 9H16.5a.5.5 0 0 0 0 1h3.082c.027.329.043.662.043 1s-.016.671-.043 1H16.5a.5.5 0 0 0 0 1h3.084a5.978 5.978 0 0 1-3.342 3.242A7.963 7.963 0 0 1 12 20a7.963 7.963 0 0 1-4.242-1.758A5.978 5.978 0 0 1 4.416 15H7.5a.5.5 0 0 0 0-1H4.418A7.963 7.963 0 0 1 4 13c0-.338.016-.671.043-1H7.5a.5.5 0 0 0 0-1H4.416a5.978 5.978 0 0 1 3.342-3.242A7.963 7.963 0 0 1 12 4zm0 2a6 6 0 1 0 0 12A6 6 0 0 0 12 6z" />
                </svg>
              );
            }
            return (
              <Tooltip
                key={link.url}
                title={
                  link.link_type.charAt(0).toUpperCase() + link.link_type.slice(1)
                }
                arrow
              >
                <Box
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={styles.socialLink}
                >
                  {icon}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Card>
  );
};

// Utility for tables: translate BYE to 'spielfrei'
export function displayTeamName(team: Team | null | undefined | string) {
  if (typeof team === "string") {
    return team === "BYE" ? "Spielfrei" : team;
  }
  return team?.name === "BYE" || !team ? "Spielfrei" : team.name;
}

export default TeamCard;
